import { motion } from 'framer-motion';
import Image from 'next/image';
import type { SwipeableHandlers } from 'react-swipeable';
import { useEffect, useRef } from 'react';

// Updated the Testimonial type to include `text` and `author` properties
type Testimonial = {
  name: string;
  message: string;
  rating: number;
  image: string;
  text: string; // Added this property
  author: string; // Added this property
};

type Props = {
  testimonials: Testimonial[];
  currentTestimonial?: number;
  onTestimonialChange?: (index: number) => void;
  swipeHandlers?: SwipeableHandlers;
  showCards?: boolean;
};

const TestimonialsSection = ({ testimonials, currentTestimonial = 0, onTestimonialChange, swipeHandlers, showCards = true }: Props) => {
  const prevIndexRef = useRef(currentTestimonial);
  const directionRef = useRef(0);

  useEffect(() => {
    const prev = prevIndexRef.current;
    const next = currentTestimonial;
    const total = testimonials.length || 1;
    // Determine direction considering wrap-around
    const forward = (next === (prev + 1) % total) || (next > prev && next - prev <= total / 2);
    directionRef.current = forward ? 1 : -1;
    prevIndexRef.current = currentTestimonial;
  }, [currentTestimonial, testimonials.length]);

  const handlePrev = () => {
    if (!onTestimonialChange || !testimonials?.length) return;
    const next = (currentTestimonial - 1 + testimonials.length) % testimonials.length;
    onTestimonialChange(next);
  };

  const handleNext = () => {
    if (!onTestimonialChange || !testimonials?.length) return;
    const next = (currentTestimonial + 1) % testimonials.length;
    onTestimonialChange(next);
  };

  const variants = {
    enter: (dir: number) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir: number) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-primary-950 via-primary-900 to-primary-950 py-24 text-white">
      <motion.div
        className="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-soft-gold/25 blur-3xl"
        animate={{ opacity: [0.25, 0.45, 0.25], scale: [0.9, 1.05, 0.92] }}
        transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="pointer-events-none absolute top-1/4 right-[-8rem] h-96 w-96 rounded-full bg-amber-300/15 blur-[120px]"
        animate={{ opacity: [0.18, 0.36, 0.18], scale: [0.85, 1.08, 0.9] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut', delay: 1.2 }}
      />
      <div className="relative z-10 container mx-auto px-6">
        <div className="relative max-w-3xl mx-auto text-center mb-16 md:mb-20">
          <motion.div
            className="pointer-events-none absolute inset-x-0 -top-10 h-24 rounded-full bg-gradient-to-r from-soft-gold/20 via-white/10 to-soft-gold/20 blur-3xl"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          />
          <motion.span
            className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm uppercase tracking-[0.3em] text-soft-gold"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Voices of Trust
          </motion.span>
          <motion.h2
            className="mt-6 text-4xl md:text-5xl font-display font-semibold"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Heartfelt stories from our extended family
          </motion.h2>
          <motion.p
            className="mt-4 text-lg text-primary-100"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Every kitten adoption begins a new chapter. Listen to the moments that made these homes shine a little brighter.
          </motion.p>
        </div>

        <div className="relative max-w-4xl mx-auto" {...(swipeHandlers || {})}>
          <motion.div
            className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-r from-white/5 via-transparent to-white/5 blur-2xl"
            animate={{ opacity: [0.2, 0.45, 0.2], x: ['-10%', '10%', '-10%'] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          />
          <div className="relative overflow-visible">
            <motion.div
              key={testimonials[currentTestimonial]?.name || currentTestimonial}
              className="relative"
              custom={directionRef.current}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <motion.div
                className="pointer-events-none absolute -inset-[1.6px] rounded-[26px] bg-gradient-to-r from-soft-gold/55 via-white/60 to-amber-200/55 opacity-70"
                animate={{ rotate: [0, 4, -2, 0] }}
                transition={{ duration: 16, repeat: Infinity, ease: 'linear' }}
              />
              <motion.div
                className="pointer-events-none absolute -inset-[1.6px] rounded-[26px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <motion.div
                  className="absolute inset-0 rounded-[26px] bg-gradient-to-r from-transparent via-white/30 to-transparent"
                  style={{ backgroundSize: '220% 100%' }}
                  animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                />
              </motion.div>
              <div className="relative rounded-[24px] border border-white/10 bg-white/6 p-10 backdrop-blur-xl shadow-[0_25px_60px_rgba(15,23,42,0.45)]">
                <motion.div
                  className="absolute -left-5 top-8 hidden md:block h-14 w-14 rounded-full bg-gradient-to-br from-soft-gold/40 via-white/20 to-transparent blur-xl"
                  animate={{ opacity: [0.4, 0.7, 0.4], scale: [0.9, 1.1, 0.95] }}
                  transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                />
                <motion.div
                  className="absolute -right-6 bottom-6 hidden md:block h-16 w-16 rounded-full bg-amber-200/30 blur-3xl"
                  animate={{ opacity: [0.35, 0.6, 0.35], scale: [0.85, 1.08, 0.9] }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1.1 }}
                />
                <div className="flex flex-col gap-6 md:flex-row md:items-start">
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-soft-gold/20 via-white/10 to-transparent blur-md" />
                    <div className="relative h-16 w-16 md:h-20 md:w-20 rounded-full border border-white/25 bg-white/5 p-1">
                      <div className="h-full w-full overflow-hidden rounded-full">
                        <Image
                          src={testimonials[currentTestimonial]?.image || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iOTYiIGhlaWdodD0iOTYiIHZpZXdCb3g9IjAgMCA5NiA5NiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI0OCIgY3k9IjQ4IiByPSI0OCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LXNpemU9IjE2IiBmaWxsPSIjOWNhNGEiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuMzVlbSI+8J+RiyA8L3RleHQ+PC9zdmc+'}
                          alt={testimonials[currentTestimonial]?.name || 'Adopter'}
                          width={96}
                          height={96}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex flex-col gap-2">
                      <h3 className="text-2xl font-semibold text-white">
                        {testimonials[currentTestimonial]?.name || 'Anonymous'}
                      </h3>
                    </div>
                    <motion.div
                      className="mt-3 flex items-center gap-1"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >
                      {[...Array(5)].map((_, i) => (
                        <motion.span
                          key={i}
                          className={`text-lg ${i < (testimonials[currentTestimonial]?.rating || 0) ? 'text-amber-300' : 'text-white/20'}`}
                          animate={{ scale: i === (testimonials[currentTestimonial]?.rating || 0) - 1 ? [1, 1.1, 1] : 1 }}
                          transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
                        >
                          ★
                        </motion.span>
                      ))}
                    </motion.div>
                    <motion.p
                      className="mt-6 text-lg leading-relaxed text-primary-100"
                      initial={{ opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.35, delay: 0.15 }}
                    >
                      “{testimonials[currentTestimonial]?.message || 'No message available'}”
                    </motion.p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Enhanced the TestimonialsSection with animations and better styling */}
          {showCards && (
            <motion.div
              className="relative"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { staggerChildren: 0.2, duration: 0.8 },
                },
              }}
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial?.name || index}
                  className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
                  variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                  }}
                >
                  <p className="text-sm text-warm-brown/70">{testimonial?.text || 'No testimonial text available'}</p>
                  <h3 className="text-lg font-semibold text-warm-brown mt-4">{testimonial?.author || testimonial?.name || 'Anonymous'}</h3>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Arrows */}
          {onTestimonialChange && testimonials?.length > 1 && (
            <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between pointer-events-none">
              <button
                aria-label="Previous testimonial"
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center"
                onClick={handlePrev}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-white"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"/></svg>
              </button>
              <button
                aria-label="Next testimonial"
                className="pointer-events-auto w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur flex items-center justify-center"
                onClick={handleNext}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="text-white"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"/></svg>
              </button>
            </div>
          )}

          {/* Dots */}
          {onTestimonialChange && testimonials?.length > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              {testimonials.map((_, idx) => (
                <button
                  key={idx}
                  aria-label={`Show testimonial ${idx + 1}`}
                  className={`h-2.5 rounded-full transition-all ${idx === currentTestimonial ? 'w-6 bg-white' : 'w-2.5 bg-white/40 hover:bg-white/60'}`}
                  onClick={() => onTestimonialChange(idx)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
